import { useEffect, useState } from 'react';
import { AppProviders } from './providers/AppProviders';
import { ProtectedAppRoute } from './routes/ProtectedAppRoute';
import { ImmersiveGraphView } from '../features/graph-view/components/ImmersiveGraphView';
import { PreferencesService } from '../core/services/preferences-service';
import { LocalStoragePreferencesRepository } from '../storage/implementations/localstorage-preferences-repository';
import { useWorkspaceModeStore } from '../features/graph-view/services/workspace-mode-store';
import type { AccessMode } from '../core/policies/access-gate/preview-node-entitlement';
import {
  clearAuthSession,
  loadStoredAuthSession,
  saveAuthSession,
  type FullModeAuthSession,
} from '../features/access-gate/services/google-auth-unlock-service';

const preferencesService = new PreferencesService(new LocalStoragePreferencesRepository());

export default function App() {
  const [authSession, setAuthSession] = useState<FullModeAuthSession | null>(() => loadStoredAuthSession());
  const [startupReady, setStartupReady] = useState(false);
  const accessMode: AccessMode = authSession ? 'unlocked' : 'preview';
  const workspaceMode = useWorkspaceModeStore((state) => state.currentMode);
  const currentGraphMode = useWorkspaceModeStore((state) => state.currentGraphMode);
  const hydrateWorkspaceMode = useWorkspaceModeStore((state) => state.hydrate);
  const isWorkspaceModeHydrated = useWorkspaceModeStore((state) => state.isHydrated);

  useEffect(() => {
    let active = true;

    void preferencesService.getWorkspaceModePreference().then((preference) => {
      if (active) {
        hydrateWorkspaceMode(preference);
      }
    });

    return () => {
      active = false;
    };
  }, [hydrateWorkspaceMode]);

  useEffect(() => {
    if (!isWorkspaceModeHydrated) {
      return;
    }

    void preferencesService.setWorkspaceModePreference({
      workspaceMode,
      lastGraphMode: currentGraphMode,
    });
  }, [currentGraphMode, isWorkspaceModeHydrated, workspaceMode]);

  function acceptAuthSession(nextAuthSession: FullModeAuthSession) {
    saveAuthSession(nextAuthSession);
    setAuthSession(nextAuthSession);
  }

  function expireAuthSession() {
    clearAuthSession();
    setAuthSession(null);
  }

  return (
    <AppProviders>
      <ProtectedAppRoute
        accessMode={accessMode}
        authSession={authSession}
        onAuthSessionAccepted={acceptAuthSession}
        onAuthSessionExpired={expireAuthSession}
        onStartupReady={() => setStartupReady(true)}
        startupReady={startupReady}
      >
        <ImmersiveGraphView accessMode={accessMode} authSession={authSession} />
      </ProtectedAppRoute>
    </AppProviders>
  );
}
